/**
 * Adapptio Core library.
 *
 * @package lib-core
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BP } from "@adapptio/lib-blueprint";
import { createBlueprintSchema } from "./BlueprintBase";
import { DOC_TYPES } from "./DocTypes";
import { termsEditor } from "../terms";

const DEFAULT_PAGE_CONTENT = "# Hello!\n\nSwitch to edit mode to edit this page contents.";

/**
 * Readme page schema
 */
export const readmePage = BP.Const.Object({
	label: termsEditor.blueprints.readme.page.label,
	description: termsEditor.blueprints.readme.page.description,
	constraints: {
		required: true
	},
	props: {
		title: BP.Prop(
			BP.Const.String({
				label: termsEditor.blueprints.readme.pageTitle.label,
				description: termsEditor.blueprints.readme.pageTitle.description,
				default: "New Page",
				fallbackValue: "New Page",
				constraints: {
					required: true
				},
				editorOptions: {
					hidden: true
				}
			})
		),
		content: BP.Prop(
			BP.Const.String({
				label: termsEditor.blueprints.readme.pageContent.label,
				description: termsEditor.blueprints.readme.pageContent.description,
				default: DEFAULT_PAGE_CONTENT,
				fallbackValue: "",
				constraints: {
					required: false
				},
				editorOptions: {
					hidden: true
				}
			})
		)
	}
});

export type TReadmePageSchema = typeof readmePage;

/**
 * Readme blueprint "spec" property schema
 */
export const readmeSpec = BP.Const.Object({
	label: termsEditor.blueprints.readme.root.label,
	constraints: {
		required: true
	},
	props: {
		pages: BP.Prop(
			BP.Const.Array({
				label: termsEditor.blueprints.readme.pages.label,
				description: termsEditor.blueprints.readme.pages.description,
				default: [
					{
						title: "Index",
						content: "# Hello!\n\nSwitch to edit mode to edit this page contents.\n\nYou can also add links to blueprint resources like [this](./views/main)."
					}
				],
				fallbackValue: [],
				constraints: {
					required: false
				},
				items: readmePage
			})
		)
	}
});

export type TBlueprintReadmeSpecSchema = typeof readmeSpec;

/**
 * Readme Blueprint Schema
 */
export const BlueprintReadme = createBlueprintSchema(DOC_TYPES.README_V1, readmeSpec);

/** Readme Blueprint Schema Type */
export type TBlueprintReadmeSchema = typeof BlueprintReadme;
